/**
 * OnboardingLaunchService — turns the /welcome draft into a REAL Genie.
 *
 * The seven-step flow used to fake this: LaunchPage navigated to a placeholder
 * id and BuildingPage stepped a checklist on timers. Nothing was created,
 * nothing was read, and the share link pointed at a page that did not exist.
 *
 * There is no new backend here. Everything below calls services that already
 * ship in this app:
 *   - `AgentService.create` (→ `handle-agent` create) is the same path the
 *     GenieSetup wizard uses, and it already creates the default activation
 *     pack for the new Genie as part of the call.
 *   - `ContentExtractionService.scrapeWebsite` (→ `doc-upload` scrape-website)
 *     is the same path the Knowledge Base "scrape a website" upload uses.
 *
 * What is genuinely missing is documented in
 * docs/handoff/welcome-onboarding-backend.md. Nothing here pretends.
 */
import { AgentService } from "@/services/agent/core";
import { ContentExtractionService } from "@/services/knowledgebase/ContentExtractionService";
import type { Agent } from "@/types/agent/agent";
import type { SetupAnalysisResult } from "@/types/agent/setup";
import type { OnboardingDraft } from "@/types/voice-vision";
import type { OnboardingKnowledgeResult } from "@/types/voice-vision/launch";

/** Voice ids used elsewhere for quick creation. Kept identical on purpose. */
const VOICE_POOL = [
  "vrZUZJRakCOEuVVvuvUo",
  "jdL78By9k0CiiRbfZ5Oc",
  "ZDiFbNPVdxBy16JfdMbf",
];

const DEFAULT_MAX_DURATION_SECONDS = 600;

/** How deep and wide the onboarding read of a website goes. */
export const ONBOARDING_SCRAPE_DEPTH = 2;
export const ONBOARDING_SCRAPE_MAX_PAGES = 40;

/** Normalise whatever the customer typed into something fetchable. */
export const normaliseWebsite = (value: string | undefined): string | null => {
  const raw = value?.trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

/** Slugify a label for the public page URL. */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Compose the Genie's instructions from what the owner actually told us.
 * Deliberately readable: this is the text the owner can edit later, and it
 * should look like something a person wrote about their business.
 */
export const buildSystemPrompt = (draft: OnboardingDraft): string => {
  const business = draft.business.name?.trim() || "this business";
  const role = draft.genieType?.title?.trim() || "front desk assistant";
  const blurb = draft.genieType?.blurb?.trim();
  const site = normaliseWebsite(draft.business.website);
  const traits = draft.personalityTraits?.length
    ? draft.personalityTraits.join(", ").toLowerCase()
    : "professional, friendly";

  const lines = [
    `You are the ${role.toLowerCase()} for ${business}.`,
    blurb ? `Your job: ${blurb}` : null,
    `Speak in a ${traits} tone. Keep answers short and useful, the way a good receptionist would on the phone.`,
    site ? `Everything you know about ${business} comes from ${site} and the documents added to your knowledge.` : null,
    `If you do not know something, say so plainly and offer to take a message or pass the caller to a person. Never guess at prices, availability or policy.`,
  ];

  return lines.filter(Boolean).join("\n\n");
};

/** The greeting a caller actually hears first. */
export const buildFirstMessage = (draft: OnboardingDraft): string => {
  const custom = draft.business.greeting?.trim();
  if (custom) return custom;
  const business = draft.business.name?.trim();
  return business
    ? `Kia ora, you've reached ${business}. How can I help?`
    : "Kia ora, how can I help?";
};

/**
 * Map the wizard draft onto the SAME payload shape the existing GenieSetup
 * flow sends. No new contract, no new edge function.
 */
export const draftToAnalysis = (draft: OnboardingDraft): SetupAnalysisResult => {
  const businessName = draft.business.name?.trim();
  const typeTitle = draft.genieType?.title?.trim();
  const genieName = businessName && typeTitle ? `${typeTitle} - ${businessName}` : typeTitle || businessName || "My Genie";

  return {
    // No setup conversation happened — this Genie was built from the wizard.
    setupConversationId: "",
    genieName,
    useCase: draft.genieType?.blurb ?? `Answer enquiries for ${businessName ?? "the business"}`,
    description: draft.genieType?.category ?? undefined,
    language: "en",
    voicePreference: draft.voiceLabel ?? "professional, clear, and friendly",
    voiceId: draft.voiceId ?? VOICE_POOL[Math.floor(Math.random() * VOICE_POOL.length)],
    systemPrompt: buildSystemPrompt(draft),
    firstMessage: buildFirstMessage(draft),
    webWelcomeMessage: buildFirstMessage(draft),
    confidence: 1,
    conversationSettings: {
      maxDuration: DEFAULT_MAX_DURATION_SECONDS,
      turnTimeout: 7,
      silenceTimeout: 300,
      turnMode: "silence",
    },
    brand: businessName || "Help Genie",
    category: draft.genieType?.category ?? undefined,
    personalityTraits: draft.personalityTraits,
    sendConversationReports: false,
  };
};

export class OnboardingLaunchService {
  /**
   * Create the real Genie. Returns the mapped Agent, which carries the genuine
   * id and (once the page row exists) the url_name the share link needs.
   *
   * The activation pack is created server-side inside this same call, so by
   * the time the owner reaches Launch the pack is already waiting.
   */
  static async createGenie(draft: OnboardingDraft): Promise<Agent> {
    const agent = await AgentService.create(draftToAnalysis(draft));
    if (!agent?.id) {
      throw new Error("The Genie was not created. Nothing was saved, so nothing to undo.");
    }
    return agent as Agent;
  }

  /**
   * Read the owner's website into the Genie's knowledge. Real work, real
   * network call, real failure mode — a website that cannot be read does NOT
   * fail the whole build, because the Genie is already useful without it.
   */
  static async readWebsite(
    websiteInput: string | undefined,
    userId: string,
    agentId: string,
  ): Promise<OnboardingKnowledgeResult> {
    const url = normaliseWebsite(websiteInput);
    if (!url) {
      return { attempted: false, ok: false, pages: null, message: "No website was given." };
    }

    try {
      const data = await ContentExtractionService.scrapeWebsite(
        url,
        userId,
        ONBOARDING_SCRAPE_DEPTH,
        ONBOARDING_SCRAPE_MAX_PAGES,
        agentId,
      );

      return {
        attempted: true,
        ok: true,
        pages: readPageCount(data),
        message: null,
      };
    } catch (error) {
      return {
        attempted: true,
        ok: false,
        pages: null,
        message:
          error instanceof Error
            ? error.message
            : "We could not read that website. You can add knowledge later.",
      };
    }
  }
}

/**
 * The scrape edge returns different shapes depending on the path it took, so
 * read the page count defensively and show nothing rather than a wrong number.
 */
const readPageCount = (data: unknown): number | null => {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const candidates = [
    record.pagesProcessed,
    record.pages_processed,
    record.pageCount,
    record.totalPages,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && candidate > 0) return candidate;
  }
  if (Array.isArray(record.documents) && record.documents.length > 0) return record.documents.length;
  if (Array.isArray(record.pages) && record.pages.length > 0) return record.pages.length;
  return null;
};
